import React, { useState } from 'react';
import { 
  MoonStar, Mail, Lock, ChevronRight, GraduationCap, 
  UserPlus, LogIn, ArrowLeft, Phone, Fingerprint, 
  CheckCircle2, MessageCircle, AlertCircle, BookOpen, ClipboardList, User, Info, Users, Shield, Camera
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useToast } from '../context/ToastContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Input, Badge } from '../components/ui';
import {
  hashPassword,
  sanitizeText,
  isValidHttpsUrl,
  validateRegistrationData,
  isLoginBlocked,
  incrementLoginAttempt,
  resetLoginAttempts,
  MAX_LOGIN_ATTEMPTS
} from '../utils/security';
import { enrollmentSchema, professorSchema } from '../lib/validation';
import * as api from '../lib/api';

export function Login() {
  const [mode, setMode] = useState('home'); // home, login, reg_type, reg_step1, reg_step2, reg_step3, reg_success, prof_reg_step1, prof_reg_step2, prof_success
  const [profile, setProfile] = useState('student');
  const [regProfile, setRegProfile] = useState('student'); // student ou professor na matrícula
  
  // Login State
  const [loginId, setLoginId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Student Registration State
  const [regData, setRegData] = useState({
    courses: [],          // array de cursos selecionados pelo aluno
    fullName: '',
    citizenId: '',
    phone: '',
    profilePicture: '',
    password: '',
    confirmPassword: ''
  });

  // Professor Registration State
  const [profRegData, setProfRegData] = useState({
    fullName: '',
    citizenId: '',
    phone: '',
    courses: '',
    profilePicture: '',
    password: '',
    confirmPassword: ''
  });

  const [enrollmentResult, setEnrollmentResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const { addToast } = useToast();
  const { allEnrollments, processEnrollment, professors, requestProfRegistration, addProfessorDirectly, courses } = useData();

  const handleLogin = async (e) => {
    e.preventDefault();

    // ─── Rate Limiting ──────────────────────────────────
    const { blocked, secondsLeft } = isLoginBlocked();
    if (blocked) {
      addToast(`Muitas tentativas incorretas. Aguarde ${secondsLeft}s antes de tentar novamente.`, 'error');
      return;
    }

    setIsLoading(true);
    let loginSuccess = false;

    try {
      // Hash da senha informada pelo usuário
      const inputHash = await hashPassword(loginPassword);

      if (profile === 'secretaria') {
        // Conta de secretaria seeded pelo DataContext com hash
        try {
          const raw = localStorage.getItem('lumenfall_secretaria_account');
          if (raw) {
            const secretariaAccount = JSON.parse(raw);
            if (
              loginId === secretariaAccount.enrollmentId &&
              inputHash === secretariaAccount.password
            ) {
              login({ ...secretariaAccount, name: secretariaAccount.fullName, type: 'secretaria' });
              addToast('Acesso ADM concedido. Bem-vindo, Secretaria!', 'success');
              loginSuccess = true;
            }
          }
        } catch { /* ignora JSON mal-formado */ }
      } else {
        // Aluno or Professor using API
        const res = await api.login(loginId, inputHash, profile);
        
        if (res.success && res.data?.user) {
          const userData = res.data.user;
          login({ 
            ...userData, 
            name: userData.fullName,
            type: profile,
            enrollmentId: userData.id // Use id as enrollmentId for compatibility
          });
          addToast(`Bem-vindo, ${userData.fullName}!`, 'success');
          loginSuccess = true;
        } else {
          // If API returns an error message, display it
          if (res.data?.error) {
            addToast(res.data.error, 'error');
          } else {
            throw new Error('Credenciais inválidas');
          }
        }
      }

      if (!loginSuccess) {
        const attempts = incrementLoginAttempt();
        const remaining = MAX_LOGIN_ATTEMPTS - attempts;
        if (remaining <= 0) {
          addToast('Conta bloqueada temporariamente por excesso de tentativas.', 'error');
        } else {
          addToast(`Credenciais inválidas. ${remaining} tentativa(s) restante(s).`, 'error');
        }
      } else {
        resetLoginAttempts();
      }
    } catch (err) {
      console.error('Erro no processo de login:', err);
      addToast(err.message || 'Erro ao processar login. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // ─── Validação e Saneamento de Inputs ────────────────────────
    try {
      enrollmentSchema.parse(regData);
    } catch (zodError) {
      if (zodError.errors && zodError.errors.length > 0) {
        addToast(zodError.errors[0].message, 'error');
      } else {
        addToast('Dados inválidos. Verifique os campos e tente novamente.', 'error');
      }
      return;
    }

    setIsLoading(true);
    try {
      // Hash da senha antes de qualquer armazenamento
      const hashedPassword = await hashPassword(regData.password);

      // Objeto saneado — remove confirmPassword, sanitiza strings
      const sanitizedData = {
        ...regData,
        fullName: sanitizeText(regData.fullName, 100),
        citizenId: sanitizeText(regData.citizenId, 30),
        phone: sanitizeText(regData.phone, 20),
        profilePicture: isValidHttpsUrl(regData.profilePicture) ? regData.profilePicture.trim() : '',
        password: hashedPassword
      };
      delete sanitizedData.confirmPassword;

      const result = await processEnrollment(sanitizedData);
      if (result.success) {
        setEnrollmentResult(result.data);
        setMode('reg_success');
        addToast('Matrícula realizada com sucesso!', 'success');
      } else {
        addToast(result.message, 'error');
      }
    } catch (err) {
      console.error('Erro ao processar matrícula:', err);
      addToast('Erro ao processar matrícula. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfRegister = async (e) => {
    e.preventDefault();
    
    // ─── Validação do Professor ────────────────────────
    try {
      professorSchema.parse(profRegData);
    } catch (zodError) {
      if (zodError.errors && zodError.errors.length > 0) {
        addToast(zodError.errors[0].message, 'error');
      } else {
        addToast('Dados inválidos. Verifique os campos.', 'error');
      }
      return;
    }

    setIsLoading(true);
    try {
      const hashedPassword = await hashPassword(profRegData.password);
      const sanitizedData = {
        ...profRegData,
        fullName: sanitizeText(profRegData.fullName, 100),
        citizenId: sanitizeText(profRegData.citizenId, 30),
        phone: sanitizeText(profRegData.phone, 20),
        courses: sanitizeText(profRegData.courses, 200),
        profilePicture: isValidHttpsUrl(profRegData.profilePicture) ? profRegData.profilePicture.trim() : '',
        password: hashedPassword
      };
      delete sanitizedData.confirmPassword;

      const result = await addProfessorDirectly(sanitizedData);
      if (result.success) {
        setEnrollmentResult(result.data);
        setMode('prof_success');
        addToast('Cadastro de professor realizado com sucesso!', 'success');
      } else {
        addToast('Erro ao processar cadastro docente.', 'error');
      }
    } catch (err) {
      console.error('Erro ao cadastrar professor:', err);
      addToast('Erro ao processar cadastro. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <main className="loginPage">
      <motion.section 
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        className="loginLeft"
      >
        <div className="glowOrb" />
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-purple rounded-xl flex items-center justify-center shadow-lg">
            <GraduationCap className="text-white" size={28} />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Lumenfall</h2>
        </div>
        
        <h1 className="text-6xl font-extrabold leading-none mb-6">
          Sua jornada <br/>
          <span className="text-purple">acadêmica</span> começa aqui.
        </h1>
        
        <p className="text-lg text-muted mb-8 max-w-md">
          Portal integrado para gestão de aprendizagem, acompanhamento de matrículas e comunicação institucional.
        </p>
        
        <div className="chips">
          <span>React 18</span>
          <span>Framer Motion</span>
          <span>Professional Flow</span>
        </div>
      </motion.section>

      <section className="loginRight">
        <AnimatePresence mode="wait">
          {mode === 'home' && (
            <motion.div key="home" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard text-center">
              <div className="loginIcon mb-6"><GraduationCap size={40}/></div>
              <h2 className="text-3xl font-black mb-2">Portal Acadêmico</h2>
              <p className="text-muted mb-8 font-medium">Seja bem-vindo à Universidade Lumenfall.</p>
              
              <div className="flex flex-col gap-4">
                <Button className="w-full py-6 flex items-center justify-center gap-3 text-lg font-black" onClick={() => { setProfile('student'); setMode('login'); }}>
                  <LogIn size={22} /> Entrar no Portal
                </Button>
                <Button variant="outline" className="w-full py-6 flex items-center justify-center gap-3 text-lg border-purple/30 text-purple font-black hover:bg-purple/5" onClick={() => { setMode('reg_type'); }}>
                  <UserPlus size={22} /> Fazer Matrícula
                </Button>
              </div>
              
              <p className="mt-8 text-xs text-muted">
                Dúvidas sobre o processo? <button onClick={() => window.open('https://discord.gg/lumenfall', '_blank')} className="text-purple font-bold hover:underline">Acesse nosso Discord oficial.</button>
              </p>
            </motion.div>
          )}

          {mode === 'login' && (
            <motion.form key="login" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard" onSubmit={handleLogin}>
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('home')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              
              <div className="loginIcon"><MoonStar size={30}/></div>
              <h2 className="text-3xl font-black mb-2">Acessar Portal</h2>
              <p className="text-sm text-muted mb-8">Entre com suas credenciais acadêmicas.</p>

              <div className="formGroup mb-6">
                <label className="text-[10px] uppercase tracking-[2px] text-muted mb-3 block font-black">Tipo de Acesso</label>
                <div className="grid grid-cols-3 gap-2 bg-[#070d19] p-1.5 rounded-2xl border border-[#1d2a42]">
                  <button 
                    type="button" 
                    className={`py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${profile === 'student' ? 'bg-purple text-white shadow-lg' : 'text-muted hover:text-white'}`}
                    onClick={() => setProfile('student')}
                  >
                    Aluno
                  </button>
                  <button 
                    type="button" 
                    className={`py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${profile === 'professor' ? 'bg-purple text-white shadow-lg' : 'text-muted hover:text-white'}`}
                    onClick={() => setProfile('professor')}
                  >
                    Docente
                  </button>
                  <button 
                    type="button" 
                    className={`py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${profile === 'secretaria' ? 'bg-purple text-white shadow-lg' : 'text-muted hover:text-white'}`}
                    onClick={() => setProfile('secretaria')}
                  >
                    Secretaria
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                <Input 
                  label={profile === 'student' ? "Número de Matrícula" : profile === 'professor' ? "Matrícula Funcional" : "Login Secretaria"}
                  icon={profile === 'student' ? GraduationCap : profile === 'professor' ? Users : Shield}
                  value={loginId} 
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder={profile === 'student' ? "MAT-XXXXXX" : profile === 'professor' ? "PROF-XXXXX" : "secretaria"}
                  required
                />

                <Input 
                  label="Senha"
                  icon={Lock}
                  type="password" 
                  value={loginPassword} 
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <Button type="submit" className="w-full mt-10 py-4 text-lg font-black" disabled={isLoading}>
                {isLoading ? 'Autenticando...' : 'Entrar no Sistema'}
              </Button>
              
              <div className="flex justify-between mt-8 text-xs text-muted font-bold">
                <button 
                  type="button" 
                  className="hover:text-purple transition-colors"
                  onClick={() => addToast('Para recuperar sua senha, procure a coordenação acadêmica.', 'info')}
                >
                  Esqueceu sua senha?
                </button>
                <button type="button" className="hover:text-purple transition-colors text-purple" onClick={() => setMode('reg_type')}>Fazer Matrícula</button>
              </div>
            </motion.form>
          )}

          {mode === 'reg_type' && (
            <motion.div key="reg_type" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard text-center">
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('home')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              <div className="loginIcon mx-auto mb-6"><UserPlus size={40}/></div>
              <h2 className="text-3xl font-black mb-2">Fazer Matrícula</h2>
              <p className="text-sm text-muted mb-8">Selecione o seu perfil de ingresso.</p>
              
              <div className="flex flex-col gap-4">
                <button 
                  className="p-6 rounded-[28px] border-2 border-white/5 bg-white/5 text-left flex items-center gap-5 hover:border-purple/30 hover:bg-purple/5 transition-all group"
                  onClick={() => { setRegProfile('student'); setMode('reg_step1'); }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-purple/10 flex items-center justify-center text-purple group-hover:bg-purple group-hover:text-white transition-all">
                    <GraduationCap size={28} />
                  </div>
                  <div>
                    <h4 className="font-black text-white text-lg">Matrícula de Aluno</h4>
                    <p className="text-xs text-muted">Ingresso em cursos de graduação.</p>
                  </div>
                  <ChevronRight size={20} className="ml-auto text-muted group-hover:text-purple transition-all" />
                </button>

                <button 
                  className="p-6 rounded-[28px] border-2 border-white/5 bg-white/5 text-left flex items-center gap-5 hover:border-purple/30 hover:bg-purple/5 transition-all group"
                  onClick={() => { setRegProfile('professor'); setMode('prof_reg_step1'); }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-purple/10 flex items-center justify-center text-purple group-hover:bg-purple group-hover:text-white transition-all">
                    <Users size={28} />
                  </div>
                  <div>
                    <h4 className="font-black text-white text-lg">Cadastro de Professor</h4>
                    <p className="text-xs text-muted">Solicitação de registro docente.</p>
                  </div>
                  <ChevronRight size={20} className="ml-auto text-muted group-hover:text-purple transition-all" />
                </button>
              </div>
            </motion.div>
          )}

          {mode === 'reg_step1' && (
            <motion.div key="reg1" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard">
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('home')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              <div className="flex items-center gap-2 text-purple mb-4">
                <Badge variant="purple" className="px-4 py-1.5 text-[10px] font-black tracking-widest uppercase">Etapa 1 de 3</Badge>
              </div>
              <h2 className="text-3xl font-black mb-2">Escolha seus Cursos</h2>
              <p className="text-sm text-muted mb-5">Selecione <strong className="text-white">um ou mais cursos</strong> que deseja cursar. Cada um tem 20 vagas (10 por turma).</p>

              {/* Contador de cursos selecionados */}
              {regData.courses.length > 0 && (
                <div className="mb-5 px-4 py-3 bg-purple/10 border border-purple/25 rounded-2xl flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-purple shrink-0" />
                  <span className="text-sm font-bold text-purple">
                    {regData.courses.length} curso{regData.courses.length > 1 ? 's' : ''} selecionado{regData.courses.length > 1 ? 's' : ''}:
                  </span>
                  <span className="text-xs text-purple/70 truncate">{regData.courses.join(' · ')}</span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                {(courses || []).filter(c => c.status === 'Ativo').map(course => {
                  const studentsInCourse = (allEnrollments || []).filter(e => {
                    const sc = Array.isArray(e.courses) && e.courses.length > 0
                      ? e.courses : [e.course || e.courseName].filter(Boolean);
                    return sc.includes(course.name);
                  });
                  const occupied = studentsInCourse.length;
                  const total = course.turmas?.reduce((sum, t) => sum + (t.vacancies || 0), 0) || 20;
                  const isFull = occupied >= total;
                  const isSelected = (regData.courses || []).includes(course.name);

                  return (
                    <button 
                      key={course.id}
                      disabled={isFull}
                      className={`p-5 rounded-2xl border-2 text-left transition-all duration-300 flex flex-col gap-3 group relative overflow-hidden ${isFull ? 'opacity-50 grayscale cursor-not-allowed border-white/5 bg-white/5' : isSelected ? 'border-purple bg-purple/10 shadow-[0_0_20px_rgba(139,92,246,0.15)]' : 'border-white/5 bg-white/5 hover:border-purple/30 hover:bg-purple/5'}`}
                      onClick={() => {
                        if (isFull) return;
                        setRegData(prev => ({
                          ...prev,
                          courses: prev.courses.includes(course.name)
                            ? prev.courses.filter(c => c !== course.name)
                            : [...prev.courses, course.name]
                        }));
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${isSelected ? 'bg-purple text-white shadow-lg shadow-purple/30' : 'bg-white/5 text-muted group-hover:bg-purple/20 group-hover:text-purple'}`}>
                          <GraduationCap size={24} />
                        </div>
                        <span className={`font-bold text-lg transition-colors ${isSelected ? 'text-white' : 'text-muted group-hover:text-purple/80'}`}>{course.name}</span>
                        </div>
                        {isSelected && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="bg-purple rounded-full p-1">
                          <CheckCircle2 size={20} className="text-white" />
                        </motion.div>
                      )}
                      </div>
                      
                      <p className="text-xs text-muted leading-relaxed font-medium line-clamp-2">
                        {course.description}
                      </p>

                      <div className="flex items-center justify-between mt-2 pt-3 border-t border-white/5">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${isFull ? 'bg-red' : 'bg-green animate-pulse'}`} />
                          <span className="text-[10px] font-black uppercase text-muted tracking-wider">
                            {isFull ? 'Turmas Lotadas' : `${total - occupied} vagas restantes`}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-muted/50">{occupied}/{total} ocupadas</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button 
                className="w-full mt-8 py-4 text-lg font-black" 
                disabled={!regData.courses || regData.courses.length === 0}
                onClick={() => setMode('reg_step2')}
              >
                Próxima Etapa <ChevronRight size={20} className="ml-2" />
              </Button>
            </motion.div>
          )}

          {mode === 'reg_step2' && (
            <motion.form key="reg2" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard" onSubmit={(e) => { e.preventDefault(); setMode('reg_step3'); }}>
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('reg_step1')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              <div className="flex items-center gap-2 text-purple mb-4">
                <Badge variant="purple" className="px-4 py-1.5 text-[10px] font-black tracking-widest uppercase">Etapa 2 de 3</Badge>
              </div>
              <h2 className="text-3xl font-black mb-2">Dados Pessoais</h2>
              <p className="text-sm text-muted mb-8">Preencha suas informações para o registro.</p>

              <div className="space-y-4">
                <Input 
                  label="Nome Completo"
                  icon={GraduationCap}
                  value={regData.fullName}
                  onChange={e => setRegData({...regData, fullName: e.target.value})}
                  required
                />
                <Input 
                  label="ID do Cidadão (Identificador)"
                  icon={Fingerprint}
                  value={regData.citizenId}
                  onChange={e => setRegData({...regData, citizenId: e.target.value})}
                  required
                />
                <Input 
                  label="Telefone de Contato"
                  icon={Phone}
                  value={regData.phone}
                  onChange={e => setRegData({...regData, phone: e.target.value})}
                  required
                />
                <Input 
                  label="Link da Foto (FiveM Manager)"
                  icon={Camera}
                  value={regData.profilePicture}
                  onChange={e => setRegData({...regData, profilePicture: e.target.value})}
                  placeholder="https://..."
                  required
                />
              </div>

              <Button type="submit" className="w-full mt-10 py-4 text-lg font-black">
                Próxima Etapa
              </Button>
            </motion.form>
          )}

          {mode === 'reg_step3' && (
            <motion.form key="reg3" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard" onSubmit={handleRegister}>
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('reg_step2')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              <div className="flex items-center gap-2 text-purple mb-4">
                <Badge variant="purple" className="px-4 py-1.5 text-[10px] font-black tracking-widest uppercase">Etapa 3 de 3</Badge>
              </div>
              <h2 className="text-3xl font-black mb-2">Criar Senha</h2>
              <p className="text-sm text-muted mb-8">Defina uma senha segura para acessar seu portal.</p>

              <div className="space-y-5">
                <Input 
                  label="Nova Senha"
                  icon={Lock}
                  type="password"
                  value={regData.password}
                  onChange={e => setRegData({...regData, password: e.target.value})}
                  required
                />
                <Input 
                  label="Confirmar Senha"
                  icon={Shield}
                  type="password"
                  value={regData.confirmPassword}
                  onChange={e => setRegData({...regData, confirmPassword: e.target.value})}
                  required
                />
              </div>

              <div className="mt-6 p-4 bg-yellow/5 border border-yellow/20 rounded-2xl flex items-start gap-3">
                <AlertCircle className="text-yellow shrink-0" size={18} />
                <p className="text-[10px] text-yellow/80 leading-relaxed">
                  Guarde sua senha com cuidado. Ela será necessária junto com o número de matrícula para seus futuros acessos.
                </p>
              </div>

              <Button type="submit" className="w-full mt-8" disabled={isLoading}>
                {isLoading ? 'Processando Matrícula...' : 'Concluir Matrícula'}
              </Button>
            </motion.form>
          )}

          {mode === 'prof_reg_step1' && (
            <motion.form key="prof_reg1" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard" onSubmit={(e) => { e.preventDefault(); setMode('prof_reg_step2'); }}>
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('home')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              <div className="flex items-center gap-2 text-purple mb-4">
                <Badge variant="purple" className="px-4 py-1.5 text-[10px] font-black tracking-widest uppercase">Cadastro Docente</Badge>
              </div>
              <h2 className="text-3xl font-black mb-2">Dados do Professor</h2>
              <p className="text-sm text-muted mb-8">Inicie sua solicitação de registro institucional.</p>

              <div className="space-y-4">
                <Input label="Nome Completo" icon={User} value={profRegData.fullName} onChange={e => setProfRegData({...profRegData, fullName: e.target.value})} required />
                <Input label="ID na Cidade" icon={Fingerprint} value={profRegData.citizenId} onChange={e => setProfRegData({...profRegData, citizenId: e.target.value})} required />
                <Input label="Telefone" icon={Phone} value={profRegData.phone} onChange={e => setProfRegData({...profRegData, phone: e.target.value})} required />
                <Input 
                  label="Link da Foto (FiveM Manager)"
                  icon={Camera}
                  value={profRegData.profilePicture}
                  onChange={e => setProfRegData({...profRegData, profilePicture: e.target.value})}
                  placeholder="https://..."
                  required
                />
                <Input label="Cursos" icon={BookOpen} value={profRegData.courses} onChange={e => setProfRegData({...profRegData, courses: e.target.value})} placeholder="Direito, Engenharia, ..." required />
              </div>

              <Button type="submit" className="w-full mt-8">Próxima Etapa</Button>
            </motion.form>
          )}

          {mode === 'prof_reg_step2' && (
            <motion.form key="prof_reg2" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard" onSubmit={handleProfRegister}>
              <button type="button" className="flex items-center gap-2 text-xs font-bold text-purple bg-purple/10 px-4 py-2 rounded-xl w-fit mb-8 hover:bg-purple/20 transition-all border border-purple/20" onClick={() => setMode('prof_reg_step1')}>
                <ArrowLeft size={14} /> Voltar
              </button>
              <h2 className="text-3xl font-black mb-2">Definir Senha</h2>
              <p className="text-sm text-muted mb-8">Segurança para sua conta docente.</p>

              <div className="space-y-5">
                <Input label="Senha" icon={Lock} type="password" value={profRegData.password} onChange={e => setProfRegData({...profRegData, password: e.target.value})} required />
                <Input label="Confirmar Senha" icon={Shield} type="password" value={profRegData.confirmPassword} onChange={e => setProfRegData({...profRegData, confirmPassword: e.target.value})} required />
              </div>

              <div className="mt-6 p-4 bg-blue/5 border border-blue/20 rounded-2xl flex items-start gap-3">
                <Info className="text-blue shrink-0" size={18} />
                <p className="text-[10px] text-blue/80 leading-relaxed">
                  Sua solicitação será enviada para aprovação da coordenação acadêmica. Após aprovado, sua matrícula funcional será gerada.
                </p>
              </div>

              <Button type="submit" className="w-full mt-8" disabled={isLoading}>{isLoading ? 'Enviando Solicitação...' : 'Solicitar Cadastro'}</Button>
            </motion.form>
          )}

          {mode === 'prof_success' && enrollmentResult && (
            <motion.div key="prof_success" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard text-center">
              <div className="w-24 h-24 bg-green/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-green/20">
                <CheckCircle2 size={48} className="text-green" />
              </div>
              <h2 className="text-3xl font-black mb-2">Solicitação Aprovada!</h2>
              <p className="text-sm text-muted mb-10 font-medium">Seu cadastro docente foi validado pela coordenação.</p>
              
              <div className="bg-white/5 border border-white/10 rounded-[32px] p-8 text-left space-y-5 mb-8">
                <div className="flex justify-between border-b border-white/5 pb-4">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">Professor</span>
                  <strong className="text-sm text-white">{enrollmentResult.fullName}</strong>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-4">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">Matrícula Funcional</span>
                  <strong className="text-purple text-lg font-mono">{enrollmentResult.enrollmentId}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">Status</span>
                  <Badge variant="green">Ativo</Badge>
                </div>
              </div>

              <Button className="w-full py-4 text-lg font-black" onClick={() => {
                setProfile('professor');
                setLoginId(enrollmentResult.enrollmentId);
                setMode('login');
              }}>
                Acessar Painel Docente
              </Button>
            </motion.div>
          )}

          {mode === 'reg_success' && enrollmentResult && (
            <motion.div key="success" variants={containerVariants} initial="initial" animate="animate" exit="exit" className="loginCard text-center">
              <div className="w-24 h-24 bg-green/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-green/20">
                <CheckCircle2 size={48} className="text-green" />
              </div>
              <h2 className="text-3xl font-black mb-2">Matrícula Realizada!</h2>
              <p className="text-sm text-muted mb-10 font-medium">Sua jornada na Universidade Lumenfall começou.</p>
              
              <div className="bg-white/5 border border-white/10 rounded-[32px] p-8 text-left space-y-5 mb-8 shadow-inner">
                <div className="flex justify-between border-b border-white/5 pb-4">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">Estudante</span>
                  <strong className="text-sm text-white">{enrollmentResult.fullName}</strong>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-4">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">Matrícula</span>
                  <strong className="text-purple text-lg font-mono">{enrollmentResult.enrollmentId}</strong>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-4">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">{enrollmentResult?.courses?.length > 1 ? 'Cursos' : 'Curso'}</span>
                  <strong className="text-sm text-white text-right max-w-[200px]">
                    {Array.isArray(enrollmentResult?.courses) && enrollmentResult.courses.length > 0
                      ? enrollmentResult.courses.join(', ')
                      : (enrollmentResult?.courseName || '—')}
                  </strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted text-[11px] uppercase font-bold tracking-widest">Turma</span>
                  <strong className="text-sm text-white">{enrollmentResult.turma}</strong>
                </div>
              </div>

              <div className="p-6 bg-[#5865F2]/10 border border-[#5865F2]/20 rounded-3xl mb-10 flex items-start gap-4 text-left">
                <MessageCircle size={24} className="text-[#5865F2] shrink-0" />
                <p className="text-xs text-[#5865F2] font-bold leading-relaxed">
                  IMPORTANTE: Anote seu número de matrícula. Ele é seu identificador único e será exigido em todos os seus acessos futuros.
                </p>
              </div>

              <Button className="w-full py-4 text-lg font-black" onClick={() => {
                setLoginId(enrollmentResult.enrollmentId);
                setMode('login');
              }}>
                Acessar Portal
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

    </main>
  );
}
